import { ScheduleItem } from '../types/scheduleItem.types';
import { TimeScale } from '../types/calendar.types';
import { calculateTopPosition, calculateHeightFromDuration, doTimeRangesOverlap } from './timeUtils';

export interface PositionedScheduleItem extends ScheduleItem {
  column: number;
  totalColumns: number;
  top: number; // pixels from start of day
  height: number; // pixel height
  width: number; // percentage (100/totalColumns)
  left: number; // percentage offset
}

interface EventGroup {
  items: ScheduleItem[];
}

// Algorithm for side-by-side overlap display (like MS Teams)
export function calculateOverlapLayout(
  items: ScheduleItem[],
  timeScaleMinutes: TimeScale,
  dayStartHour: number = 0
): PositionedScheduleItem[] {
  if (items.length === 0) return [];
  
  // Sort by start time
  const sorted = [...items].sort((a, b) => 
    a.startDateTime.getTime() - b.startDateTime.getTime()
  );
  
  // Group overlapping items into clusters
  const clusters: EventGroup[] = [];
  let currentCluster: EventGroup = { items: [sorted[0]] };
  let clusterEndTime = sorted[0].endDateTime;
  
  for (let i = 1; i < sorted.length; i++) {
    const item = sorted[i];
    
    // Check if this item overlaps with any item in current cluster
    const overlapsCluster = currentCluster.items.some(clusterItem =>
      doTimeRangesOverlap(
        item.startDateTime, item.endDateTime,
        clusterItem.startDateTime, clusterItem.endDateTime
      )
    );
    
    if (overlapsCluster) {
      currentCluster.items.push(item);
      // Extend cluster end time if needed
      if (item.endDateTime > clusterEndTime) {
        clusterEndTime = item.endDateTime;
      }
    } else {
      // Start new cluster
      clusters.push(currentCluster);
      currentCluster = { items: [item] };
      clusterEndTime = item.endDateTime;
    }
  }
  
  // Don't forget the last cluster
  clusters.push(currentCluster);
  
  // Position items within each cluster
  const positioned: PositionedScheduleItem[] = [];
  
  for (const cluster of clusters) {
    // Sort cluster by start time for consistent column assignment
    cluster.items.sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());
    
    // Assign columns using greedy algorithm
    // Each column is a list of events that don't overlap with each other
    const columns: ScheduleItem[][] = [];
    
    for (const item of cluster.items) {
      let placed = false;
      
      // Try to place in existing column where it doesn't overlap with the last item
      for (let colIndex = 0; colIndex < columns.length; colIndex++) {
        const column = columns[colIndex];
        const lastItem = column[column.length - 1];
        
        // Check if item starts after (or at the same time as) last item in column ends
        // We use >= to allow back-to-back meetings
        if (item.startDateTime >= lastItem.endDateTime) {
          column.push(item);
          placed = true;
          break;
        }
      }
      
      // If not placed, create new column
      if (!placed) {
        columns.push([item]);
      }
    }
    
    // Calculate positions for items
    const totalColumns = columns.length;
    const columnWidth = 100 / totalColumns;
    
    for (let colIndex = 0; colIndex < columns.length; colIndex++) {
      for (const item of columns[colIndex]) {
        positioned.push({
          ...item,
          column: colIndex,
          totalColumns,
          top: calculateTopPosition(item.startDateTime, timeScaleMinutes, dayStartHour),
          height: calculateHeightFromDuration(item.startDateTime, item.endDateTime, timeScaleMinutes),
          width: columnWidth,
          left: colIndex * columnWidth,
        });
      }
    }
  }
  
  return positioned;
}

// Group schedule items by day for calendar views
export function groupItemsByDay(items: ScheduleItem[]): Map<string, ScheduleItem[]> {
  const grouped = new Map<string, ScheduleItem[]>();
  
  for (const item of items) {
    const dateKey = item.startDateTime.toISOString().split('T')[0];
    
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    
    grouped.get(dateKey)!.push(item);
  }
  
  return grouped;
}

// Get items for a specific day with overlap positioning
export function getPositionedItemsForDay(
  items: ScheduleItem[],
  date: Date,
  timeScaleMinutes: TimeScale
): PositionedScheduleItem[] {
  // Filter items that overlap with this day
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  
  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);
  
  const dayItems = items.filter(item => 
    item.startDateTime < dayEnd && item.endDateTime > dayStart
  );
  
  // Calculate overlap layout
  return calculateOverlapLayout(dayItems, timeScaleMinutes, 0);
}
